<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import {
  ArrowRight,
  Banknote,
  Check,
  Disc3,
  Headphones,
  Music2,
  Radio,
  Sparkles,
} from "@lucide/vue";
import MetricCard from "@/components/MetricCard.vue";
import TrackArtwork from "@/components/TrackArtwork.vue";
import { api } from "@/lib/api";
import type { DashboardStats, Track } from "@/types";

const stats = ref<DashboardStats>({
  tracks: 0,
  released: 0,
  plays: 0,
  estimatedRevenue: 0,
  thisWeekPlays: 0,
});
const tracks = ref<Track[]>([]);

onMounted(async () => {
  try {
    const [summary, catalog] = await Promise.all([api.dashboard(), api.tracks()]);
    stats.value = summary;
    tracks.value = catalog.tracks.slice(0, 3);
  } catch {
    // 首页仍提供完整的创作入口；连接状态会在设置页解释。
  }
});

const workflow = computed(() => [
  { label: "生成母带", done: stats.value.tracks > 0 },
  { label: "补齐发行元数据", done: stats.value.released > 0 },
  { label: "提交发行商审核", done: stats.value.released > 0 },
  { label: "建立首轮推广链接", done: stats.value.plays > 0 },
]);
</script>

<template>
  <div class="page-stack">
    <section class="hero-panel">
      <div class="hero-copy">
        <span class="status-badge"><span /> AI STUDIO ONLINE</span>
        <h2>从一个念头，<br /><em>到一首能发行的歌。</em></h2>
        <p>
          MiniMax Music 3.0 负责创作，声浪工坊把歌词、母带、发行元数据和增长任务串成一条工作流。
        </p>
        <div class="hero-actions">
          <router-link class="button button-lime" to="/create">
            <Sparkles :size="17" /> 生成第一首作品
          </router-link>
          <router-link class="text-link" to="/release">
            查看发行路径 <ArrowRight :size="15" />
          </router-link>
        </div>
      </div>
      <div class="hero-visual" aria-label="声波可视化装饰">
        <div class="hero-session-card">
          <span class="session-status"><i /> LIVE CREATIVE ENGINE</span>
          <div class="session-disc">
            <span><Music2 :size="22" /></span>
          </div>
          <div class="session-copy">
            <small>NOW BUILDING</small>
            <strong>下一首代表作</strong>
            <span>IDEA → MASTER → RELEASE</span>
          </div>
          <div class="session-progress"><i /></div>
        </div>
        <div class="wave-bars">
          <i v-for="height in [18, 31, 45, 70, 53, 82, 64, 36, 55, 28, 18]" :key="height" :style="{ height: `${height}px` }" />
        </div>
        <span class="hero-stamp">CREATE<br />RELEASE<br />GROW</span>
      </div>
    </section>

    <section class="metrics-grid">
      <MetricCard label="作品总数" :value="stats.tracks" hint="已持久保存的 AI 母带" :icon="Disc3" tone="lime" />
      <MetricCard label="累计播放" :value="stats.plays.toLocaleString()" hint="公开试听页记录" :icon="Headphones" tone="cream" />
      <MetricCard label="发行中" :value="stats.released" hint="已标记上架的作品" :icon="Radio" tone="mint" />
      <MetricCard label="估算收益" :value="`¥${stats.estimatedRevenue.toFixed(2)}`" hint="模型估算，非平台结算" :icon="Banknote" tone="coral" />
    </section>

    <section class="dashboard-grid">
      <article class="panel panel-large">
        <div class="section-heading">
          <div>
            <p class="eyebrow">RECENT MASTERS</p>
            <h3>最近作品</h3>
          </div>
          <router-link class="text-link" to="/library">查看全部 <ArrowRight :size="14" /></router-link>
        </div>

        <div v-if="tracks.length" class="track-list">
          <div v-for="(track, index) in tracks" :key="track.id" class="track-row">
            <TrackArtwork :title="track.title" :index="index" />
            <div class="track-main">
              <strong>{{ track.title }}</strong>
              <span>{{ track.genre }} · {{ new Date(track.createdAt).toLocaleDateString("zh-CN") }}</span>
            </div>
            <span class="status-tag">{{ track.status === "released" ? "已发行" : "待发行" }}</span>
            <audio class="mini-audio" :src="track.audioUrl" controls preload="none" />
          </div>
        </div>
        <div v-else class="empty-state">
          <span class="empty-icon"><Disc3 :size="28" /></span>
          <strong>曲库还是空的</strong>
          <p>生成后的高音质 WAV 会自动保存到这里，不受 MiniMax 临时链接 24 小时有效期影响。</p>
          <router-link class="button button-dark button-sm" to="/create">去创作</router-link>
        </div>
      </article>

      <article class="panel">
        <div class="section-heading">
          <div>
            <p class="eyebrow">MONEY PATH</p>
            <h3>变现路径</h3>
          </div>
          <span class="score-badge">{{ workflow.filter((item) => item.done).length }}/4</span>
        </div>
        <div class="workflow-list">
          <div v-for="(item, index) in workflow" :key="item.label" :class="{ done: item.done }">
            <span class="step-number"><Check v-if="item.done" :size="14" /><template v-else>0{{ index + 1 }}</template></span>
            <span>{{ item.label }}</span>
          </div>
        </div>
        <div class="notice-card">
          <strong>真实收益需要发行商账户</strong>
          <p>本站负责生成、留存与打包；正式上架仍需你在发行商完成实名、税务和收款配置。</p>
        </div>
      </article>
    </section>
  </div>
</template>
